import React, { useEffect, useState } from 'react';
import { getAllContactInfo, getAllServiceZones, getAllServices, ContactInfo as ContactInfoType, ServiceZone, Service } from '../services/contentService';
import { openWhatsApp } from '../services/leadService';

const WHATSAPP_PHONE = '525528447262';

const FALLBACK_SERVICES = [
  { name: 'Vestidos', icon: '👗' },
  { name: 'Conjuntos de Cocina', icon: '👩‍🍳' },
  { name: 'Ropa de Cama', icon: '🛏️' },
  { name: 'Cortinas', icon: '🪟' },
  { name: 'Reparaciones', icon: '✂️' },
  { name: 'Trajes Folklóricos', icon: '💃' },
  { name: 'Otro', icon: '✨' },
];

const ContactoPage: React.FC = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfoType[]>([]);
  const [zones, setZones] = useState<ServiceZone[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    servicio: '',
    mensaje: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Construir mensaje para WhatsApp
    let mensaje = `Hola! Me gustaría contactar con GE-NO MODA:\n\n`;
    if (formData.nombre) mensaje += `👤 *Nombre:* ${formData.nombre}\n`;
    if (formData.telefono) mensaje += `📱 *Teléfono:* ${formData.telefono}\n`;
    if (formData.email) mensaje += `✉️ *Email:* ${formData.email}\n`;
    if (formData.servicio) mensaje += `🎨 *Servicio:* ${formData.servicio}\n`;
    if (formData.mensaje) mensaje += `\n💬 *Mensaje:*\n${formData.mensaje}`;
    
    // Registra el lead en n8n -> Google Sheets (fire-and-forget) y abre WhatsApp.
    openWhatsApp(WHATSAPP_PHONE, mensaje, {
      nombre: formData.nombre,
      telefono: formData.telefono,
      email: formData.email,
      servicio: formData.servicio,
      mensaje: formData.mensaje,
      paginaOrigen: 'contacto-form',
    });

    // Limpiar formulario
    setFormData({
      nombre: '',
      telefono: '',
      email: '',
      servicio: '',
      mensaje: ''
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactData, zonesData, servicesData] = await Promise.all([
          getAllContactInfo(),
          getAllServiceZones(),
          getAllServices()
        ]);
        // Filter contact info - exclude phone and whatsapp (we'll show them as action buttons)
        const displayContact = contactData.filter(c => ['email', 'hours', 'address'].includes(c.type));
        setContactInfo(displayContact);
        setZones(zonesData);
        const regularServices = servicesData.filter(s => !s.metadata || !s.metadata.includes('"type":"custom"'));
        setServices(regularServices);
      } catch (error) {
        console.error('Error loading contact data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Page Header */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-white to-pink-50/50" />
        <div className="absolute top-10 right-20 w-48 h-48 bg-rose-400/10 rounded-full blur-[80px]" />
        <div className="absolute bottom-10 left-20 w-64 h-64 bg-pink-400/10 rounded-full blur-[80px]" />
        
        <div className="container-custom relative text-center">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-pink-500 mb-3 animate-fade-in">Contáctanos</h3>
          <h1 className="text-4xl md:text-5xl font-bold font-display text-gray-900 mb-4 animate-slide-up">
            Estamos para servirte
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto animate-slide-up-delayed">
            Cuéntanos sobre tu proyecto y te daremos una cotización personalizada
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 pb-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6 stagger-children">
              {/* WhatsApp Button - Prominent */}
              <button
                type="button"
                onClick={() => openWhatsApp(WHATSAPP_PHONE, undefined, { paginaOrigen: 'contacto-card' })}
                className="group block w-full text-left bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/20 hover:-translate-y-1 transition-all duration-500 text-white"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-3xl">💬</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Chatea con Nosotros</h3>
                    <p className="text-green-50 text-sm">¡Respuesta inmediata por WhatsApp!</p>
                  </div>
                  <div className="ml-auto">
                    <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>

              {/* Contact Cards */}
              {contactInfo.map((item) => (
                <div key={item.id} className="group bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-6 hover:shadow-lg hover:shadow-pink-500/5 hover:border-pink-200/30 transition-all duration-500">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                      <span className="text-xl">{item.icon || '📞'}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.label}</h3>
                      {item.value.split('\n').map((line, i) => (
                        <p key={i} className="text-gray-500 text-sm">{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Coverage zones */}
              {zones.length > 0 && (
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">📍</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Zona de Cobertura</h3>
                      <div className="flex flex-wrap gap-2">
                        {zones.map((zone) => (
                          <span key={zone.id} className="px-3 py-1.5 bg-pink-50/80 text-pink-600 text-sm rounded-xl border border-pink-100/50 font-medium">
                            {zone.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Contact Form - Glass */}
            <div className="lg:col-span-3">
              <div className="bg-white/60 backdrop-blur-lg rounded-3xl border border-white/50 shadow-xl shadow-pink-500/5 p-8 md:p-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 font-display">Envíanos un Mensaje</h2>
                <p className="text-gray-500 text-sm mb-8">Completa el formulario y te responderemos lo antes posible</p>
                
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                      <input 
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400 transition-all duration-300 hover:border-gray-300 placeholder-gray-400" 
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                      <input 
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400 transition-all duration-300 hover:border-gray-300 placeholder-gray-400" 
                        placeholder="Tu teléfono"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email (opcional)</label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400 transition-all duration-300 hover:border-gray-300 placeholder-gray-400" 
                      placeholder="tu@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Servicio de Interés</label>
                    <button
                      type="button"
                      onClick={() => setShowServiceModal(true)}
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-left flex items-center justify-between hover:border-pink-300 transition-all duration-300"
                    >
                      <span className={formData.servicio ? 'text-gray-800' : 'text-gray-400'}>
                        {formData.servicio || 'Selecciona un servicio...'}
                      </span>
                      <span className="text-gray-400">▾</span>
                    </button>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mensaje</label>
                    <textarea 
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleInputChange}
                      rows={4} 
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400 transition-all duration-300 hover:border-gray-300 placeholder-gray-400 resize-vertical" 
                      placeholder="Describe tu proyecto o consulta..."
                    ></textarea>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-semibold shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <span className="text-xl">💬</span>
                    Enviar por WhatsApp
                  </button>
                  <p className="text-xs text-center text-gray-500 mt-2">
                    Al enviar, se abrirá WhatsApp con tu mensaje listo
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mini Modal - Selección de Servicio */}
      {showServiceModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={() => setShowServiceModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Bottom Sheet */}
          <div
            className="relative w-full bg-white rounded-t-3xl shadow-2xl z-10 max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-6 pt-2 pb-4 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800 text-center">¿Qué servicio te interesa?</h3>
              <p className="text-sm text-gray-400 text-center mt-1">Toca para seleccionar</p>
            </div>

            {/* Grid de servicios — scrollable si hay muchos */}
            <div className="px-4 pb-2 overflow-y-auto flex-1">
              <div className="grid grid-cols-3 gap-3 pb-2">
                {(services.length > 0
                  ? [...services.map(s => ({ name: s.name, icon: s.icon || '✨' })), { name: 'Otro', icon: '✨' }]
                  : FALLBACK_SERVICES
                ).map(({ name, icon }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, servicio: name }));
                      setShowServiceModal(false);
                    }}
                    className={`flex flex-col items-center gap-2 py-4 px-2 rounded-2xl border-2 transition-all duration-150 active:scale-95 touch-manipulation
                      ${formData.servicio === name
                        ? 'border-pink-400 bg-pink-50 text-pink-700'
                        : 'border-gray-100 bg-gray-50 text-gray-700'
                      }`}
                  >
                    <span className="text-3xl leading-none">{icon}</span>
                    <span className="text-xs font-medium text-center leading-tight">{name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Cancelar — fijo al fondo */}
            <div className="px-4 pt-3 pb-8 flex-shrink-0 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowServiceModal(false)}
                className="w-full py-4 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-base active:bg-gray-200 transition-colors touch-manipulation"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactoPage;